import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  sendPasswordResetOTPAsync,
  verifyPasswordResetOTPAsync,
  resetPasswordWithOTPAsync,
  selectPasswordResetOtpSent,
  selectPasswordResetOtpVerified,
  selectPasswordResetTempToken,
  selectError,
  selectPasswordReset,
} from '../authSlice';
import {
  EnvelopeIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

export default function ForgotPassword() {
  const dispatch = useDispatch();
  const passwordResetOtpSent = useSelector(selectPasswordResetOtpSent);
  const passwordResetOtpVerified = useSelector(selectPasswordResetOtpVerified);
  const tempToken = useSelector(selectPasswordResetTempToken);
  const error = useSelector(selectError);
  const passwordReset = useSelector(selectPasswordReset);

  const [step, setStep] = useState(1); // Step 1: Email, Step 2: OTP, Step 3: New Password
  const [email, setEmail] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [canResendOTP, setCanResendOTP] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset: resetForm,
  } = useForm();

  // Countdown timer for OTP expiration
  useEffect(() => {
    if (step === 2 && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, step]);

  // Resend OTP cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Move to step 2 when OTP is sent
  useEffect(() => {
    if (passwordResetOtpSent && step === 1) {
      setStep(2);
      setTimeLeft(600);
      setCanResendOTP(false);
    }
  }, [passwordResetOtpSent, step]);

  // Move to step 3 when OTP is verified
  useEffect(() => {
    if (passwordResetOtpVerified && step === 2) {
      setStep(3);
      resetForm();
    }
  }, [passwordResetOtpVerified, step, resetForm]);

  // Redirect on successful password reset
  if (passwordReset) {
    return <Navigate to="/login" replace={true}></Navigate>;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSendOTP = (data) => {
    setEmail(data.email);
    dispatch(sendPasswordResetOTPAsync(data.email));
  };

  const handleVerifyOTP = (data) => {
    dispatch(
      verifyPasswordResetOTPAsync({
        email,
        otp: data.otp,
      })
    );
  };

  const handleResetPassword = (data) => {
    dispatch(
      resetPasswordWithOTPAsync({
        email,
        password: data.password,
        tempToken,
      })
    );
  };

  const handleResendOTP = () => {
    setCanResendOTP(false);
    setResendCooldown(30);
    setTimeLeft(600);
    dispatch(sendPasswordResetOTPAsync(email));
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-xl shadow-subtle border border-slate-200 p-8 sm:p-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              className="mx-auto h-12 w-auto"
              src="/ecommerce.png"
              alt="SubifyHub"
            />
            <h2 className="mt-6 text-2xl font-light tracking-tight text-slate-900">
              Reset Password
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {step === 1 && 'Enter your email to receive an OTP'}
              {step === 2 && 'Enter the OTP sent to your email'}
              {step === 3 && 'Create your new password'}
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8 flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                    s < step
                      ? 'bg-emerald-500 text-white'
                      : s === step
                      ? 'bg-sky-500 text-white'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {s < step ? '✓' : s}
                </div>
                {s < 3 && (
                  <div
                    className={`h-1 flex-1 mx-2 rounded-full transition-all ${
                      s < step ? 'bg-emerald-500' : 'bg-slate-200'
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-800 text-sm">
                {typeof error === 'string' ? error : error.message || 'An error occurred'}
              </p>
            </div>
          )}

          {/* Step 1: Email */}
          {step === 1 && (
            <form
              noValidate
              onSubmit={handleSubmit(handleSendOTP)}
              className="space-y-5"
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b/gi,
                        message: 'Please enter a valid email',
                      },
                    })}
                    type="email"
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                Send OTP
              </button>
            </form>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <form
              noValidate
              onSubmit={handleSubmit(handleVerifyOTP)}
              className="space-y-5"
            >
              <div>
                <label
                  htmlFor="otp"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Enter OTP
                </label>
                <input
                  id="otp"
                  {...register('otp', {
                    required: 'OTP is required',
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: 'OTP must be 6 digits',
                    },
                  })}
                  type="text"
                  maxLength="6"
                  className="w-full px-4 py-3 text-center text-2xl tracking-widest bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                  placeholder="000000"
                />
                {errors.otp && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.otp.message}
                  </p>
                )}
              </div>

              {/* Timer and Resend */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {timeLeft > 0 ? (
                    <>
                      OTP expires in <span className="font-semibold text-sky-600">{formatTime(timeLeft)}</span>
                    </>
                  ) : (
                    <span className="text-red-600 font-semibold">OTP Expired</span>
                  )}
                </span>
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase"
              >
                <CheckCircleIcon className="w-4 h-4" />
                Verify OTP
              </button>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendCooldown > 0}
                className={`w-full py-3 px-6 rounded-xl font-medium text-sm transition-all duration-200 ${
                  resendCooldown > 0
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-100 text-sky-600 hover:bg-slate-200'
                }`}
              >
                {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form
              noValidate
              onSubmit={handleSubmit(handleResetPassword)}
              className="space-y-5"
            >
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="password"
                    {...register('password', {
                      required: 'Password is required',
                      pattern: {
                        value:
                          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/gm,
                        message:
                          'Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="w-full pl-10 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ShieldCheckIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value, formValues) =>
                        value === formValues.password || 'Passwords do not match',
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="w-full pl-10 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase"
              >
                <CheckCircleIcon className="w-4 h-4" />
                Reset Password
              </button>
            </form>
          )}

          {/* Back to Login Link */}
          {step === 1 && (
            <div className="mt-8 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-500 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          )}
        </div>

        {/* Trust Badge */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            Secure password reset protected with industry-standard encryption
          </p>
        </div>
      </div>
    </div>
  );
}
